<template>
  <teleport to="#app">
    <div
      v-if="isVisible"
      ref="popoverElement"
      :class="['custom-popover', customClass]"
      :style="popoverStyle"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <div class="popover-arrow" v-if="arrow" :style="arrowStyle"></div>
      <div class="popover-content word-break">
        <slot></slot>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'

const props = defineProps({
  target: {
    type: String,
    required: true
  },
  triggers: {
    type: String,
    default: 'hover'
  },
  placement: {
    type: String,
    default: 'top'
  },
  arrow: {
    type: Boolean,
    default: true
  },
  customClass: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['show', 'hide'])

const isVisible = ref(false)
const popoverElement = ref(null)
const targetElement = ref(null)
const position = ref({ x: 0, y: 0 })
const arrowPosition = ref({ x: 0, y: 0 })

let hoverTimeout = null

const popoverStyle = computed(() => ({
  position: 'absolute',
  left: `${position.value.x}px`,
  top: `${position.value.y}px`,
  zIndex: 1060
}))

const arrowStyle = computed(() => ({
  left: `${arrowPosition.value.x}px`
}))

const updatePosition = async () => {
  if (!targetElement.value || !popoverElement.value) return

  await nextTick()

  const targetRect = targetElement.value.getBoundingClientRect()
  const popoverRect = popoverElement.value.getBoundingClientRect()

  // Add scroll position to get absolute positioning
  const scrollX = window.pageXOffset || document.documentElement.scrollLeft
  const scrollY = window.pageYOffset || document.documentElement.scrollTop

  let x = 0
  let y = 0
  let arrowX = 0

  switch (props.placement) {
    case 'top':
      x = targetRect.left + scrollX + (targetRect.width / 2) - (popoverRect.width / 2)
      y = targetRect.top + scrollY - popoverRect.height - 8
      arrowX = (popoverRect.width / 2) - 6
      break
    case 'bottom':
      x = targetRect.left + scrollX + (targetRect.width / 2) - (popoverRect.width / 2)
      y = targetRect.bottom + scrollY + 8
      arrowX = (popoverRect.width / 2) - 6
      break
    case 'left':
      x = targetRect.left + scrollX - popoverRect.width - 8
      y = targetRect.top + scrollY + (targetRect.height / 2) - (popoverRect.height / 2)
      break
    case 'right':
      x = targetRect.right + scrollX + 8
      y = targetRect.top + scrollY + (targetRect.height / 2) - (popoverRect.height / 2)
      break
  }

  // Keep popover within viewport (considering scroll position)
  const padding = 8
  const maxX = scrollX + window.innerWidth - popoverRect.width - padding
  const maxY = scrollY + window.innerHeight - popoverRect.height - padding

  x = Math.max(scrollX + padding, Math.min(x, maxX))
  y = Math.max(scrollY + padding, Math.min(y, maxY))

  position.value = { x, y }
  arrowPosition.value = { x: arrowX, y: 0 }
}

const showPopover = () => {
  clearTimeout(hoverTimeout)
  isVisible.value = true
  emit('show')
  nextTick(() => {
    updatePosition()
  })
}

const hidePopover = () => {
  clearTimeout(hoverTimeout)
  hoverTimeout = setTimeout(() => {
    isVisible.value = false
    emit('hide')
  }, 100)
}

const onMouseEnter = () => {
  clearTimeout(hoverTimeout)
}

const onMouseLeave = () => {
  hidePopover()
}

const handleTargetMouseEnter = () => {
  if (props.triggers === 'hover') {
    showPopover()
  }
}

const handleTargetMouseLeave = () => {
  if (props.triggers === 'hover') {
    hidePopover()
  }
}

const handleTargetClick = () => {
  if (props.triggers === 'click') {
    if (isVisible.value) {
      hidePopover()
    } else {
      showPopover()
    }
  }
}

const handleClickOutside = (event) => {
  if (
    targetElement.value &&
    !targetElement.value.contains(event.target) &&
    popoverElement.value &&
    !popoverElement.value.contains(event.target)
  ) {
    isVisible.value = false
    emit('hide')
  }
}

const setupEventListeners = () => {
  if (!targetElement.value) return

  if (props.triggers === 'hover') {
    targetElement.value.addEventListener('mouseenter', handleTargetMouseEnter)
    targetElement.value.addEventListener('mouseleave', handleTargetMouseLeave)
  } else if (props.triggers === 'click') {
    targetElement.value.addEventListener('click', handleTargetClick)
    document.addEventListener('click', handleClickOutside)
  }
}

const removeEventListeners = () => {
  if (!targetElement.value) return

  if (props.triggers === 'hover') {
    targetElement.value.removeEventListener('mouseenter', handleTargetMouseEnter)
    targetElement.value.removeEventListener('mouseleave', handleTargetMouseLeave)
  } else if (props.triggers === 'click') {
    targetElement.value.removeEventListener('click', handleTargetClick)
    document.removeEventListener('click', handleClickOutside)
  }
}

watch(() => props.target, () => {
  removeEventListeners()
  nextTick(() => {
    targetElement.value = document.getElementById(props.target)
    setupEventListeners()
  })
})

onMounted(() => {
  targetElement.value = document.getElementById(props.target)
  setupEventListeners()

  window.addEventListener('scroll', updatePosition, true)
  window.addEventListener('resize', updatePosition)
})

onUnmounted(() => {
  removeEventListeners()
  window.removeEventListener('scroll', updatePosition, true)
  window.removeEventListener('resize', updatePosition)
  clearTimeout(hoverTimeout)
})

defineExpose({
  show: showPopover,
  hide: hidePopover
})
</script>

<style scoped lang="scss">
.custom-popover {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  max-width: 276px;
  font-size: 0.875rem;

  .popover-content {
    padding: 12px 16px;
  }

  .popover-arrow {
    position: absolute;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid #fff;
    bottom: -6px;

    &::before {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border-left: 7px solid transparent;
      border-right: 7px solid transparent;
      border-top: 7px solid rgba(0, 0, 0, 0.15);
      bottom: 1px;
      left: -7px;
    }
  }

  &.placement-bottom .popover-arrow {
    border-top: none;
    border-bottom: 6px solid #fff;
    top: -6px;
    bottom: auto;

    &::before {
      border-top: none;
      border-bottom: 7px solid rgba(0, 0, 0, 0.15);
      top: 1px;
      bottom: auto;
    }
  }

  &.placement-left .popover-arrow,
  &.placement-right .popover-arrow {
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    top: 50%;
    transform: translateY(-50%);
  }

  &.placement-left .popover-arrow {
    border-left: 6px solid #fff;
    right: -6px;

    &::before {
      border-left: 7px solid rgba(0, 0, 0, 0.15);
      right: 1px;
      top: -7px;
    }
  }

  &.placement-right .popover-arrow {
    border-right: 6px solid #fff;
    left: -6px;

    &::before {
      border-right: 7px solid rgba(0, 0, 0, 0.15);
      left: 1px;
      top: -7px;
    }
  }
}
</style>
